import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Check, Globe, Lock, Scale, Settings, LogOut, Monitor, Moon, Sun, User } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuItem,
} from '@/components/ui/dropdown-menu'
import { useAuthStore } from '@/stores/authStore'
import { useSettingsStore } from '@/stores/settingsStore'
import { useVaultStore } from '@/stores/vaultStore'
import { cn } from '@/lib/utils'
import * as kc from '@/lib/keycloak'

const LANGUAGES = [
  { value: 'fr', labelKey: 'settings.languageFrench' },
  { value: 'en', labelKey: 'settings.languageEnglish' },
]

const THEMES = [
  { value: 'light', labelKey: 'settings.themeLight', icon: Sun },
  { value: 'dark', labelKey: 'settings.themeDark', icon: Moon },
  { value: 'system', labelKey: 'settings.themeSystem', icon: Monitor },
]

export function Header() {
  const { t } = useTranslation()
  const { authenticated, profile } = useAuthStore()
  const theme = useSettingsStore((s) => s.theme)
  const setTheme = useSettingsStore((s) => s.setTheme)
  const language = useSettingsStore((s) => s.language)
  const setLanguage = useSettingsStore((s) => s.setLanguage)
  const lockAll = useVaultStore((s) => s.lockAll)
  const hasUnlocked = useVaultStore((s) => Object.keys(s.unlocked).length > 0)

  return (
    <header className="sticky top-0 z-40 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-24 items-center gap-4">
        <Link to="/" className="flex items-center gap-2">
          <img src="/locky-logo.png" alt="Locky" className="h-24 w-auto" onError={(e) => {
            // fallback : si /locky-logo.png n'existe pas, on affiche l'icône cadenas + texte
            e.currentTarget.outerHTML = '<span class="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-primary-foreground"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg></span><span class="font-semibold">Locky</span>'
          }} />
        </Link>

        <div className="ml-auto flex items-center gap-2">
          {hasUnlocked && (
            <Button variant="outline" size="sm" onClick={lockAll} className="gap-2">
              <Lock className="h-4 w-4" />
              <span className="hidden sm:inline">{t('vaults.lockAll')}</span>
            </Button>
          )}

          <Button variant="ghost" size="icon" asChild>
            <Link to="/legal" aria-label={t('legal.title')}>
              <Scale className="h-5 w-5" />
            </Link>
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label={t('settings.theme')}>
                <Sun className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuLabel>{t('settings.theme')}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {THEMES.map((item) => {
                const ThemeIcon = item.icon

                return (
                  <DropdownMenuItem key={item.value} onClick={() => setTheme(item.value)}>
                    <ThemeIcon className="h-4 w-4" />
                    {t(item.labelKey)}
                    <Check className={cn('ml-auto h-4 w-4', theme === item.value ? 'opacity-100' : 'opacity-0')} />
                  </DropdownMenuItem>
                )
              })}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label={t('settings.language')}>
                <Globe className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              <DropdownMenuLabel>{t('settings.language')}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {LANGUAGES.map((item) => (
                <DropdownMenuItem key={item.value} onClick={() => setLanguage(item.value)}>
                  <Check className={cn('h-4 w-4', language === item.value ? 'opacity-100' : 'opacity-0')} />
                  {t(item.labelKey)}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {authenticated ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label={t('auth.account')}>
                  <User className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">{profile?.name || profile?.username}</span>
                    {profile?.email && <span className="text-xs text-muted-foreground">{profile.email}</span>}
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/settings"><Settings className="h-4 w-4" />{t('nav.settings')}</Link>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => kc.accountManagement()}>
                  <User className="h-4 w-4" />{t('auth.keycloakAccount')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => kc.logout()} className="text-destructive">
                  <LogOut className="h-4 w-4" />{t('auth.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button onClick={() => kc.login()} size="sm">{t('auth.signIn')}</Button>
          )}
        </div>
      </div>
    </header>
  )
}
